import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { Id, Point, Project } from '@elec/domain';
import type { MessageKey, Translate } from '@elec/i18n';
import type { PanelTab } from './BottomPanel.tsx';
import {
  CHAPTERS,
  CLOSE_VIEW,
  TOUR_STEPS,
  type DemoCtx,
  type Rect,
  type Spot,
  type TourApp,
  type TourStep,
} from './tourSteps.ts';
import type { SidebarState } from './Sidebar.tsx';
import './tour.css';

/**
 * Guided tutorial over the real editor. Each step highlights a part of the screen, explains it,
 * gives a best-practice tip and shows it with a cursor that uses the real interface.
 *
 * A step is made of parts: one paragraph and the bit of demo that shows it. The card shows one
 * part at a time and plays its demo when the user moves to it with the arrows (back restores the
 * project as it was before that part). After the last part, the card shows every paragraph; a
 * click on one plays its part again. The user can try things themselves between parts.
 */

interface Snapshot {
  project: Project;
  sheetId: Id;
  panel: PanelTab | null;
  memo: Record<string, Id>;
  spot: { spot: Spot | null; caption: string | null };
  sidebar: SidebarState;
  paletteFolded: boolean;
}

const PAD = 6;
const GAP = 14;
const MARGIN = 12;
/** Height of the top bar: captions stay below it. */
const TOP_BAR = 56;

const nextFrame = () => new Promise<void>((r) => requestAnimationFrame(() => r()));

const ease = (k: number) => (k < 0.5 ? 2 * k * k : 1 - (-2 * k + 2) ** 2 / 2);

/** Set a form control's value the way typing does, so React sees the change. */
function setNativeValue(el: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement, value: string) {
  const proto =
    el instanceof HTMLSelectElement
      ? HTMLSelectElement.prototype
      : el instanceof HTMLTextAreaElement
        ? HTMLTextAreaElement.prototype
        : HTMLInputElement.prototype;
  Object.getOwnPropertyDescriptor(proto, 'value')?.set?.call(el, value);
  el.dispatchEvent(new Event(el instanceof HTMLSelectElement ? 'change' : 'input', { bubbles: true }));
}

function visibleRect(el: Element | null): Rect | null {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width < 1 && r.height < 1) return null;
  return { left: r.left, top: r.top, width: r.width, height: r.height };
}

/** Clip to the window, so a spot on a large sheet does not run off screen. */
function clip(r: Rect): Rect | null {
  const left = Math.max(r.left, 2);
  const top = Math.max(r.top, 2);
  const right = Math.min(r.left + r.width, window.innerWidth - 2);
  const bottom = Math.min(r.top + r.height, window.innerHeight - 2);
  return right - left < 2 || bottom - top < 2
    ? null
    : { left, top, width: right - left, height: bottom - top };
}

/** Card next to the highlighted rectangle, on the side with room; inside it when it is too big. */
function placeCard(
  spot: Rect | null,
  w: number,
  h: number,
  centered: boolean,
): { left: number; top: number } {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const clampX = (x: number) => Math.max(MARGIN, Math.min(x, vw - w - MARGIN));
  const clampY = (y: number) => Math.max(MARGIN, Math.min(y, vh - h - MARGIN));
  if (!spot || centered) return { left: clampX((vw - w) / 2), top: clampY((vh - h) / 2) };
  const r = {
    left: spot.left - PAD,
    top: spot.top - PAD,
    right: spot.left + spot.width + PAD,
    bottom: spot.top + spot.height + PAD,
  };
  if (vw - r.right - GAP >= w + MARGIN) return { left: r.right + GAP, top: clampY(r.top) };
  if (r.left - GAP >= w + MARGIN) return { left: r.left - GAP - w, top: clampY(r.top) };
  if (vh - r.bottom - GAP >= h + MARGIN) return { left: clampX(r.left), top: r.bottom + GAP };
  if (r.top - GAP >= h + MARGIN) return { left: clampX(r.left), top: r.top - GAP - h };
  // Too big to go beside (the sheet): inside, in its bottom-right corner.
  return { left: clampX(r.right - w - 16), top: clampY(r.bottom - h - 16) };
}

/** `**bold**`, and lines starting with "- " as a list. */
function rich(text: string): ReactNode {
  const inline = (s: string) =>
    s
      .split(/(\*\*[^*]+\*\*)/g)
      .map((part, i) =>
        part.startsWith('**') && part.endsWith('**') ? <strong key={i}>{part.slice(2, -2)}</strong> : part,
      );
  const blocks: ReactNode[] = [];
  let list: string[] = [];
  const flush = () => {
    if (list.length)
      blocks.push(
        <ul key={blocks.length}>
          {list.map((l, i) => (
            <li key={i}>{inline(l)}</li>
          ))}
        </ul>,
      );
    list = [];
  };
  for (const line of text.split('\n')) {
    if (line.startsWith('- ')) list.push(line.slice(2));
    else {
      flush();
      if (line.trim()) blocks.push(<p key={blocks.length}>{inline(line)}</p>);
    }
  }
  flush();
  return blocks;
}

export function Tour(props: {
  app: TourApp;
  t: Translate;
  /** "⌘" or "Ctrl+". */
  mod: string;
  onClose(finished: boolean): void;
  onRestart(): void;
}) {
  const { app, t, mod } = props;
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const step = TOUR_STEPS[index]!;
  const last = index === TOUR_STEPS.length - 1;

  const holeRef = useRef<HTMLDivElement>(null);
  const shadeRef = useRef<HTMLDivElement>(null);
  const captionRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const cursorRef = useRef<HTMLDivElement>(null);
  const keyRef = useRef<HTMLDivElement>(null);
  const spot = useRef<{ spot: Spot | null; caption: string | null }>({ spot: null, caption: null });
  const fast = useRef(false);
  const memo = useRef<Record<string, Id>>({});
  const cursor = useRef<Point>({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
  const centeredRef = useRef(false);
  centeredRef.current = !!step.centered;
  const appRef = useRef(app);
  appRef.current = app;
  const p = (s: string) => s.replaceAll('{mod}', mod);
  const tx = (key: string) => t(key as MessageKey, { mod });

  // ---------- highlight, caption and card follow their target every frame ----------
  useEffect(() => {
    let frame = 0;
    const resolve = (s: Spot | null): Rect | null => {
      if (!s) return null;
      const r = typeof s === 'string' ? visibleRect(document.querySelector(s)) : s(appRef.current);
      return r ? clip(r) : null;
    };
    const tick = () => {
      const r = resolve(spot.current.spot);
      const hole = holeRef.current;
      const shade = shadeRef.current;
      const cap = captionRef.current;
      const card = cardRef.current;
      if (hole && shade) {
        if (r) {
          hole.style.display = 'block';
          hole.style.left = `${r.left - PAD}px`;
          hole.style.top = `${r.top - PAD}px`;
          hole.style.width = `${r.width + 2 * PAD}px`;
          hole.style.height = `${r.height + 2 * PAD}px`;
          shade.style.display = 'none';
        } else {
          hole.style.display = 'none';
          shade.style.display = 'block';
        }
      }
      if (cap) {
        if (r && spot.current.caption) {
          cap.style.display = 'block';
          if (cap.textContent !== spot.current.caption) cap.textContent = spot.current.caption;
          const h = cap.offsetHeight;
          const left = Math.max(MARGIN, Math.min(r.left - PAD, window.innerWidth - cap.offsetWidth - MARGIN));
          // Above the spot (clear of the top bar), else below it, else inside it (large spots).
          const big = r.height > window.innerHeight * 0.45;
          const above = r.top - PAD - 8 - h;
          const below = r.top + r.height + PAD + 8;
          const top = big
            ? r.top + 10
            : above >= TOP_BAR
              ? above
              : below + h <= window.innerHeight - MARGIN
                ? below
                : r.top + 10;
          cap.style.left = `${big ? r.left + 10 : left}px`;
          cap.style.top = `${top}px`;
        } else cap.style.display = 'none';
      }
      if (card) {
        const pos = placeCard(r, card.offsetWidth, card.offsetHeight, centeredRef.current);
        // Never over the caption: move down below it.
        if (cap && cap.style.display !== 'none') {
          const c = cap.getBoundingClientRect();
          const w = card.offsetWidth;
          const h = card.offsetHeight;
          const overlaps =
            pos.left < c.right && pos.left + w > c.left && pos.top < c.bottom && pos.top + h > c.top;
          if (overlaps) pos.top = Math.min(c.bottom + 8, window.innerHeight - h - MARGIN);
        }
        card.style.left = `${Math.round(pos.left)}px`;
        card.style.top = `${Math.round(pos.top)}px`;
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, []);

  // ---------- the demo's hands ----------
  const ctx = useMemo<DemoCtx>(() => {
    const a = () => appRef.current;
    const wait = (ms: number) =>
      fast.current
        ? Promise.resolve()
        : new Promise<void>((res) => {
            const t0 = performance.now();
            const f = () => (fast.current || performance.now() - t0 >= ms ? res() : requestAnimationFrame(f));
            requestAnimationFrame(f);
          });
    const setCursor = (x: number, y: number) => {
      cursor.current = { x, y };
      if (cursorRef.current) cursorRef.current.style.transform = `translate(${x}px, ${y}px)`;
    };
    const showCursor = () => cursorRef.current?.classList.add('on');
    /** Move the cursor to client (x, y); `onFrame` gets the point of each frame. */
    const moveTo = (x: number, y: number, onFrame?: (pt: Point) => void) => {
      showCursor();
      const from = { ...cursor.current };
      const dist = Math.hypot(x - from.x, y - from.y);
      const dur = Math.max(280, Math.min(900, 200 + dist * 0.7));
      if (fast.current || dist < 1) {
        setCursor(x, y);
        onFrame?.({ x, y });
        return Promise.resolve();
      }
      return new Promise<void>((res) => {
        const t0 = performance.now();
        const f = (now: number) => {
          const k = Math.min(1, (now - t0) / dur);
          if (k >= 1 || fast.current) {
            setCursor(x, y);
            onFrame?.({ x, y });
            return res();
          }
          const e = ease(k);
          const pt = { x: from.x + (x - from.x) * e, y: from.y + (y - from.y) * e };
          setCursor(pt.x, pt.y);
          onFrame?.(pt);
          requestAnimationFrame(f);
        };
        requestAnimationFrame(f);
      });
    };
    const press = async () => {
      const c = cursorRef.current;
      if (!c) return;
      c.classList.remove('press');
      void c.offsetWidth; // restart the animation
      c.classList.add('press');
      await wait(160);
    };
    /** Client pixels → sheet millimetres (the view is a scale and an offset). */
    const toSheet = (pt: Point): Point => {
      const o = a().toClient(0, 0);
      const u = a().toClient(100, 100);
      return { x: ((pt.x - o.x) / (u.x - o.x)) * 100, y: ((pt.y - o.y) / (u.y - o.y)) * 100 };
    };
    /** Move to a sheet point, telling the editor where the pointer is (ghost, wire preview, drag). */
    const moveSheet = async (target: Point) => {
      const c = a().toClient(target.x, target.y);
      await moveTo(c.x, c.y, (pt) => a().editor.pointerMove(toSheet(pt)));
      a().editor.pointerMove(target);
    };
    const reach = async (selector: string): Promise<HTMLElement | null> => {
      const el = document.querySelector<HTMLElement>(selector);
      if (!el) return null;
      el.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
      const r = visibleRect(el);
      if (r) await moveTo(r.left + Math.min(r.width / 2, 40), r.top + r.height / 2);
      return el;
    };
    const ensureTool = async (kind: 'select' | 'wire') => {
      const ed = a().editor;
      if (ed.tool.kind === kind) return;
      if (kind === 'wire' && (await ctxObj.click('[data-testid="tool-wire"]'))) return;
      ed.setTool({ kind });
    };
    const ctxObj: DemoCtx = {
      get app() {
        return a();
      },
      get memo() {
        return memo.current;
      },
      spot(s, caption) {
        spot.current = { spot: s, caption: caption ? t(caption, { mod }) : null };
      },
      wait,
      async click(selector) {
        const el = await reach(selector);
        if (!el) return false;
        await press();
        el.click();
        return true;
      },
      async type(selector, text, opts) {
        const el = (await reach(selector)) as HTMLInputElement | null;
        if (!el) return false;
        await press();
        el.focus({ preventScroll: true });
        if (fast.current) setNativeValue(el, text);
        else
          for (let i = 1; i <= text.length; i++) {
            setNativeValue(el, text.slice(0, i));
            await wait(55);
          }
        await wait(250);
        if (opts?.commit) el.blur();
        return true;
      },
      fill(selector, text) {
        const el = document.querySelector<HTMLInputElement>(selector);
        if (el) setNativeValue(el, text);
      },
      async choose(selector, value) {
        const el = (await reach(selector)) as HTMLSelectElement | null;
        if (!el) return false;
        await press();
        setNativeValue(el, value);
        await wait(300);
        return true;
      },
      async key(label) {
        const k = keyRef.current;
        if (k) {
          k.textContent = p(label);
          k.classList.remove('on');
          void k.offsetWidth;
          k.classList.add('on');
        }
        await wait(700);
      },
      closeMenus() {
        // Menus close on a mouse press outside them.
        document.body.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
      },
      async place(symbolId, at) {
        const ed = a().editor;
        const before = new Set(Object.keys(ed.project.elements));
        if (!(await ctxObj.click(`.palette [data-symbol="${symbolId}"]`)))
          ed.setTool({ kind: 'place', symbolId });
        await wait(120);
        await moveSheet(at);
        await press();
        ed.pointerDown(at);
        ed.pointerUp(at);
        await wait(250);
        ed.setTool({ kind: 'select' });
        return Object.keys(ed.project.elements).find((id) => !before.has(id));
      },
      async wire(points) {
        await ensureTool('wire');
        const ed = a().editor;
        for (const pt of points) {
          await moveSheet(pt);
          await press();
          ed.pointerDown(pt);
          ed.pointerUp(pt);
          await wait(150);
        }
      },
      async sheetClick(pt) {
        await ensureTool('select');
        const ed = a().editor;
        await moveSheet(pt);
        await press();
        ed.pointerDown(pt);
        ed.pointerUp(pt);
      },
      async drag(from, to) {
        await ensureTool('select');
        const ed = a().editor;
        await moveSheet(from);
        await press();
        ed.pointerDown(from);
        await wait(120);
        await moveSheet(to);
        ed.pointerUp(to);
      },
      sample(k) {
        return t(`tour.sample.${k}` as MessageKey);
      },
    };
    return ctxObj;
  }, [t, mod]);

  // ---------- steps and parts ----------
  const parts = step.parts ?? [];
  /** Part shown on the card. */
  const [part, setPart] = useState(0);
  /** Every paragraph shown (after the last part, and for steps without parts). */
  const [showAll, setShowAll] = useState(true);
  /** Project before each step, and before each part of the current step. */
  const stepSnaps = useRef(new Map<number, Snapshot>());
  const partSnaps = useRef<Snapshot[]>([]);
  /** Last part of the current step whose demo has been applied (-1: none). */
  const played = useRef(-1);
  const current = useRef(0);
  const chain = useRef<Promise<void>>(Promise.resolve());

  const snapshot = (): Snapshot => ({
    project: app.editor.project,
    sheetId: app.editor.sheetId,
    panel: app.panel(),
    memo: { ...memo.current },
    spot: spot.current,
    sidebar: app.sidebar(),
    paletteFolded: app.paletteFolded(),
  });
  const restore = (s: Snapshot) => {
    ctx.closeMenus();
    app.closeDialogs();
    const ed = app.editor;
    ed.setTool({ kind: 'select' });
    ed.select([]);
    if (ed.project !== s.project) ed.history.reset(s.project);
    if (s.project.sheets[s.sheetId]) ed.setSheet(s.sheetId);
    app.setPanel(s.panel);
    memo.current = { ...s.memo };
    spot.current = s.spot;
    app.setSidebar(s.sidebar);
    app.setPaletteFolded(s.paletteFolded);
  };
  const frameView = (st: TourStep) => {
    if (st.view === 'close') appRef.current.zoomToArea(CLOSE_VIEW);
    else if (st.view === 'page') appRef.current.fitPage();
  };

  /**
   * Run actions one after the other. A new action first makes the animation in progress finish
   * at once (its changes are still made).
   */
  const enqueue = (fn: () => Promise<void>) => {
    fast.current = true;
    chain.current = chain.current
      .then(async () => {
        fast.current = false;
        await fn();
      })
      .catch((e: unknown) => console.warn('[tour]', e));
    return chain.current;
  };

  const runPart = async (st: TourStep, k: number, instant: boolean) => {
    partSnaps.current[k] = snapshot();
    if (instant) fast.current = true;
    frameView(st);
    await nextFrame();
    await st.parts?.[k]?.demo?.(ctx);
    played.current = k;
    if (instant) fast.current = false;
  };
  /** Show part k of the current step and play it (`review`: back to all paragraphs after). */
  const showPart = async (k: number, review: boolean) => {
    const st = TOUR_STEPS[current.current]!;
    const n = st.parts?.length ?? 0;
    if (k < 0 || k >= n) return;
    if (k <= played.current) {
      restore(partSnaps.current[k]!);
      played.current = k - 1;
    }
    while (played.current < k - 1) await runPart(st, played.current + 1, true);
    setPart(k);
    setShowAll(false);
    setPlaying(true);
    await runPart(st, k, false);
    // A field the demo typed into keeps no focus: the arrow keys go on working.
    const active = document.activeElement as HTMLElement | null;
    if (active?.closest('input, textarea, select') && !active.closest('.tour')) active.blur();
    cursorRef.current?.classList.remove('on');
    setPlaying(false);
    if (review || k === n - 1) setShowAll(true);
  };
  const openStep = async (i: number) => {
    current.current = i;
    partSnaps.current = [];
    played.current = -1;
    spot.current = { spot: null, caption: null };
    setIndex(i);
    setPart(0);
    const st = TOUR_STEPS[i]!;
    if (st.parts?.length) await showPart(0, false);
    else {
      setShowAll(true);
      frameView(st);
    }
  };
  /** Next step: the parts not watched are applied at once. Back or again: as before the step. */
  const goStep = (i: number) =>
    enqueue(async () => {
      const from = current.current;
      if (i > from) {
        const st = TOUR_STEPS[from]!;
        while (played.current < (st.parts?.length ?? 0) - 1) await runPart(st, played.current + 1, true);
        ctx.closeMenus();
        stepSnaps.current.set(i, snapshot());
      } else restore(stepSnaps.current.get(i)!);
      await openStep(i);
    });
  const goPart = (k: number, review = false) => enqueue(() => showPart(k, review));

  // Keyboard: ← → move through the parts, then on to the previous or next step.
  const keyNav = useRef<(dir: -1 | 1) => void>(() => undefined);
  keyNav.current = (dir) => {
    const n = parts.length;
    if (n && !showAll && part + dir >= 0 && part + dir < n) void goPart(part + dir);
    else if (dir > 0 && !last) void goStep(index + 1);
    else if (dir < 0 && index > 0) void goStep(index - 1);
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
      const typing = (e.target as Element | null)?.closest?.('input, textarea, select, [contenteditable]');
      if (typing) return;
      // Before the editor sees it (arrows also nudge the selection).
      e.preventDefault();
      e.stopPropagation();
      keyNav.current(e.key === 'ArrowRight' ? 1 : -1);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);

  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void enqueue(async () => {
      stepSnaps.current.set(0, snapshot());
      await openStep(0);
    });
  }, []);

  const close = async (finished: boolean) => {
    fast.current = true;
    await chain.current;
    ctx.closeMenus();
    props.onClose(finished);
  };
  const restart = () =>
    enqueue(async () => {
      props.onRestart();
      memo.current = {};
      stepSnaps.current.clear();
      stepSnaps.current.set(0, snapshot());
      await openStep(0);
    });

  const chapterLabel = `${t('tour.chapter', { n: step.chapter, total: CHAPTERS })} · ${tx(`tour.ch${step.chapter}`)}`;
  return (
    <div className="tour" data-testid="tour">
      <div className="tour-shade" ref={shadeRef} />
      <div className="tour-hole" ref={holeRef} />
      <div className="tour-caption" ref={captionRef} />
      {playing && <div className="tour-blocker" onMouseDown={(e) => e.preventDefault()} />}
      <div className="tour-cursor" ref={cursorRef} aria-hidden="true">
        <svg width="22" height="26" viewBox="0 0 22 26">
          <path d="M2 2 L2 20 L7 15.5 L10.5 23.5 L14 22 L10.6 14.2 L17.5 14.2 Z" />
        </svg>
      </div>
      <div className="tour-key" ref={keyRef} aria-hidden="true" />
      <div
        className={`tour-card${step.centered ? ' centered' : ''}`}
        ref={cardRef}
        role="dialog"
        aria-modal="false"
        aria-labelledby="tour-title"
        data-step={step.id}
      >
        <div className="tour-head">
          <span className="tour-chapter">{chapterLabel}</span>
          <span className="tour-count" data-testid="tour-count">
            {index + 1} / {TOUR_STEPS.length}
          </span>
          <button
            className="tour-x"
            onClick={() => void close(false)}
            aria-label={t('tour.close')}
            title={t('tour.close')}
          >
            ×
          </button>
        </div>
        <div className="tour-progress" aria-hidden="true">
          <span style={{ width: `${((index + 1) / TOUR_STEPS.length) * 100}%` }} />
        </div>
        <h3 id="tour-title">{tx(`tour.${step.id}.title`)}</h3>
        {step.centered || !parts.length ? (
          <div className="tour-body">{rich(tx(`tour.${step.id}.body`))}</div>
        ) : showAll ? (
          <div className="tour-parts" data-testid="tour-parts">
            {parts.map((_, k) => (
              <button
                key={k}
                className={`tour-part${k === part ? ' current' : ''}`}
                onClick={() => void goPart(k, true)}
                title={t('tour.playPart')}
                data-testid={`tour-part-${k}`}
              >
                <span className="tour-part-no">{k + 1}</span>
                <span className="tour-part-text">{rich(tx(`tour.${step.id}.p${k + 1}`))}</span>
              </button>
            ))}
          </div>
        ) : (
          <>
            <div className="tour-body tour-now" key={`${step.id}-${part}`} data-testid="tour-now">
              {rich(tx(`tour.${step.id}.p${part + 1}`))}
            </div>
            <div className="tour-nav">
              <button
                className="tour-arrow"
                onClick={() => void goPart(part - 1)}
                disabled={part === 0}
                aria-label={t('tour.prevPart')}
                title={`${t('tour.prevPart')} (←)`}
                data-testid="tour-prev"
              >
                ‹
              </button>
              <span className="tour-dots">
                {parts.map((_, k) => (
                  <button
                    key={k}
                    className={`tour-dot${k === part ? ' on' : k <= played.current ? ' seen' : ''}`}
                    onClick={() => void goPart(k)}
                    aria-label={`${k + 1} / ${parts.length}`}
                  />
                ))}
              </span>
              <span className="tour-part-count">
                {part + 1} / {parts.length}
              </span>
              <button
                className={`tour-arrow${playing ? '' : ' cue'}`}
                onClick={() => void goPart(part + 1)}
                disabled={part === parts.length - 1}
                aria-label={t('tour.nextPart')}
                title={`${t('tour.nextPart')} (→)`}
                data-testid="tour-fwd"
              >
                ›
              </button>
            </div>
          </>
        )}
        {step.keys && (
          <div className="tour-keys">
            {step.keys.map((k, i) => (
              <Fragment key={k}>
                {i > 0 && ' '}
                <kbd>{p(k)}</kbd>
              </Fragment>
            ))}
          </div>
        )}
        {showAll && (
          <div className="tour-tip" data-testid="tour-tip">
            <strong>{t('tour.tipLabel')}</strong>
            {rich(tx(`tour.${step.id}.tip`))}
          </div>
        )}
        <div className="tour-actions">
          {index > 0 && !last && (
            <button onClick={() => void goStep(index - 1)} data-testid="tour-back">
              {t('tour.back')}
            </button>
          )}
          {playing ? (
            <button
              onClick={() => {
                fast.current = true;
              }}
              data-testid="tour-skip"
            >
              <span className="tour-live" aria-hidden="true" />
              {t('tour.skipDemo')}
            </button>
          ) : (
            parts.length > 0 && (
              <button onClick={() => void goStep(index)} data-testid="tour-replay">
                {t('tour.replay')}
              </button>
            )
          )}
          <span className="grow" />
          {last ? (
            <>
              <button onClick={() => void restart()} data-testid="tour-restart">
                {t('tour.restart')}
              </button>
              <button className="primary" onClick={() => void close(true)} data-testid="tour-finish">
                {t('tour.finish')}
              </button>
            </>
          ) : (
            <button
              className={showAll ? 'primary' : ''}
              onClick={() => void goStep(index + 1)}
              data-testid="tour-next"
            >
              {index === 0 ? t('tour.begin') : t('tour.next')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
